import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Tag, Phone, Share2 } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WhatsAppFloat } from '../components/WhatsAppFloat';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await axios.get(`${API}/articles/${slug}`);
        setArticle(response.data);
      } catch (err) {
        console.error('Failed to fetch article:', err);
        setError('Article not found');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.excerpt,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const handleBookAppointment = () => {
    window.location.href = 'tel:+919986671810';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6]">
        <Header />
        <main className="pt-24 pb-20">
          <div className="section-container">
            <div className="animate-pulse max-w-3xl mx-auto">
              <div className="h-4 bg-gray-200 rounded w-24 mb-4" />
              <div className="h-10 bg-gray-200 rounded w-full mb-4" />
              <div className="h-64 bg-gray-200 rounded-2xl mb-6" />
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded w-full" />
                <div className="h-4 bg-gray-200 rounded w-full" />
                <div className="h-4 bg-gray-200 rounded w-3/4" />
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-[#F9F8F6]">
        <Header />
        <main className="pt-24 pb-20">
          <div className="section-container">
            <div className="text-center py-20">
              <h1 className="heading-2 text-[#2D4A3E] mb-4">Article Not Found</h1>
              <p className="text-[#6B6B6B] mb-8">
                The article you're looking for doesn't exist or has been removed.
              </p>
              <Link
                to="/articles"
                className="inline-flex items-center gap-2 text-[#C67D63] font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Articles
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="article-detail-page" className="min-h-screen bg-[#F9F8F6]">
      <div className="grain-overlay" />
      <Header />

      <main className="pt-24 pb-20">
        <article className="section-container">
          {/* Back Link */}
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 text-[#6B6B6B] hover:text-[#2D4A3E] mb-8"
            data-testid="back-to-articles"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Articles
          </Link>

          {/* Article Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            {/* Meta */}
            <div className="flex items-center gap-4 mb-4 text-sm text-[#6B6B6B]">
              <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-[#E6E2D8]">
                <Tag className="w-3.5 h-3.5" />
                {article.category}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(article.created_at)}
              </span>
            </div>

            {/* Title */}
            <h1
              className="heading-1 text-[#2D4A3E] mb-6"
              data-testid="article-title"
            >
              {article.title}
            </h1>

            {/* Excerpt */}
            <p className="body-large text-[#6B6B6B] mb-8">{article.excerpt}</p>

            {/* Share Button */}
            <button
              onClick={handleShare}
              className="inline-flex items-center gap-2 text-[#2D4A3E] font-medium hover:text-[#C67D63] mb-8"
              data-testid="share-btn"
            >
              <Share2 className="w-4 h-4" />
              Share this article
            </button>
          </motion.div>

          {/* Featured Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-12"
          >
            <img
              src={
                article.image_url ||
                'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=1200'
              }
              alt={article.title}
              className="w-full max-w-4xl rounded-3xl shadow-lg"
              data-testid="article-image"
            />
          </motion.div>

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-3xl"
          >
            <div
              className="prose"
              dangerouslySetInnerHTML={{ __html: article.content }}
              data-testid="article-content"
            />

            {/* CTA */}
            <div className="mt-12 p-8 bg-[#F5F2EB] rounded-2xl">
              <h3 className="heading-3 text-[#2D4A3E] mb-4">
                Ready to Start Your Healing Journey?
              </h3>
              <p className="text-[#6B6B6B] mb-6">
                Our experienced Ayurvedic physicians can help you achieve optimal
                health through personalized treatments.
              </p>
              <Button
                onClick={handleBookAppointment}
                data-testid="article-cta-btn"
                className="bg-[#2D4A3E] hover:bg-[#1e3329] text-white px-8 py-4 rounded-full flex items-center gap-2 text-lg font-medium shadow-lg"
              >
                <Phone className="w-5 h-5" />
                Book Consultation
              </Button>
            </div>
          </motion.div>
        </article>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
